/**
 * Canvas overlay pour stylet (S Pen, Apple Pencil, doigt en mode dessin).
 *
 * Posé en `absolute inset-0` au-dessus de PdfPage. Capture les pointer
 * events (W3C Pointer Events) et dessine les traits en temps réel.
 *
 * Stratégie de tracking :
 * - Le S Pen est détecté via `event.pointerType === "pen"` : toujours
 *   dessine quand activé.
 * - Le doigt (`"touch"`) dessine seulement si `forceDraw` est vrai
 *   (toggle UI). Sinon les événements passent au scroll natif.
 * - La souris dessine toujours quand le canvas est actif.
 *
 * Coordonnées : tous les points sont convertis en pourcentage 0-1 de
 * la page (résilient au zoom). Le rendu reconvertit selon le `size`
 * courant du canvas.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { StrokeDraftInput, StrokeTool } from '../lib/strokes';

export type LocalStroke = {
  /** UUID temporaire jusqu'à la persistance, ou l'UUID serveur ensuite. */
  id: string;
  /** True si pas encore sync au backend. */
  pending: boolean;
  /** True si supprimé par la gomme (à effacer du canvas mais pas du buffer
   *  tant que le DELETE serveur n'a pas répondu). */
  deleted?: boolean;
  /** Pour les strokes serveur uniquement — UUID Postgres. */
  serverId?: string;
  draft: StrokeDraftInput;
};

type Props = {
  pageNumber: number;
  /** Strokes déjà persistés OU en mémoire pour cette page. Ré-render
   *  complet à chaque changement. */
  strokes: LocalStroke[];
  /** Outil actif : pen / highlighter / eraser. */
  tool: StrokeTool | 'eraser';
  /** Couleur stylo / surligneur. */
  color: string;
  /** Épaisseur en CSS px (à scale=1). */
  width: number;
  /** Si vrai, le doigt dessine aussi (sinon scroll natif). */
  forceDraw: boolean;
  /** Activer/désactiver complètement la capture (mode lecture). */
  enabled: boolean;
  /** Appelé quand un stroke est terminé. */
  onStrokeComplete: (stroke: StrokeDraftInput) => void;
  /** Appelé quand la gomme touche un stroke existant. */
  onStrokeErase: (localId: string) => void;
};

/** Distance point-segment au carré (pour la gomme). */
function distPointSegmentSq(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number
): number {
  const abx = bx - ax;
  const aby = by - ay;
  const apx = px - ax;
  const apy = py - ay;
  const len2 = abx * abx + aby * aby;
  let t = len2 > 0 ? (apx * abx + apy * aby) / len2 : 0;
  t = Math.max(0, Math.min(1, t));
  const dx = ax + t * abx - px;
  const dy = ay + t * aby - py;
  return dx * dx + dy * dy;
}

export default function StrokeCanvas({
  pageNumber: _pageNumber,
  strokes,
  tool,
  color,
  width,
  forceDraw,
  enabled,
  onStrokeComplete,
  onStrokeErase,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const drawingRef = useRef<{
    points: [number, number, number, number][];
    pointerId: number;
    startedAt: number;
  } | null>(null);

  // Suivre la taille du parent (qui est positionné par le PdfPage).
  useLayoutEffect(() => {
    const parent = containerRef.current?.parentElement;
    if (!parent) return;
    const ro = new ResizeObserver(() => {
      const r = parent.getBoundingClientRect();
      setSize({ w: Math.round(r.width), h: Math.round(r.height) });
    });
    ro.observe(parent);
    const r = parent.getBoundingClientRect();
    setSize({ w: Math.round(r.width), h: Math.round(r.height) });
    return () => ro.disconnect();
  }, []);

  // Render — re-draw complet du canvas à chaque changement de strokes/taille.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || size.w === 0) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.floor(size.w * dpr);
    canvas.height = Math.floor(size.h * dpr);
    canvas.style.width = `${size.w}px`;
    canvas.style.height = `${size.h}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size.w, size.h);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (const s of strokes) {
      if (s.deleted) continue;
      drawStroke(ctx, s.draft, size.w, size.h);
    }
  }, [strokes, size.w, size.h]);

  function pointFromEvent(e: PointerEvent): [number, number, number, number] {
    const canvas = canvasRef.current!;
    const r = canvas.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width; // 0-1
    const y = (e.clientY - r.top) / r.height; // 0-1
    const p = e.pointerType === 'pen' ? Math.max(0.05, e.pressure || 0.5) : 0.5;
    const tNow = performance.now();
    const t = drawingRef.current ? tNow - drawingRef.current.startedAt : 0;
    return [x, y, p, t];
  }

  function shouldCapture(e: PointerEvent): boolean {
    if (!enabled) return false;
    if (e.pointerType === 'pen') return true;
    if (e.pointerType === 'mouse') return true;
    // touch → seulement si forceDraw
    return forceDraw;
  }

  function onPointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    const ne = e.nativeEvent;
    if (!shouldCapture(ne)) return;
    e.preventDefault();
    e.stopPropagation();
    const canvas = canvasRef.current!;
    canvas.setPointerCapture(ne.pointerId);
    if (tool === 'eraser') {
      // Erase un stroke par hit-test
      eraseAt(ne);
      drawingRef.current = { points: [], pointerId: ne.pointerId, startedAt: performance.now() };
    } else {
      const p = pointFromEvent(ne);
      drawingRef.current = {
        points: [p],
        pointerId: ne.pointerId,
        startedAt: performance.now(),
      };
      // Visualiser le 1er point immédiatement
      const ctx = canvas.getContext('2d');
      if (ctx) {
        const sx = p[0] * size.w;
        const sy = p[1] * size.h;
        ctx.beginPath();
        ctx.fillStyle = color;
        ctx.globalAlpha = tool === 'highlighter' ? 0.35 : 1.0;
        ctx.arc(sx, sy, (width * (p[2] || 1)) / 2, 0, 2 * Math.PI);
        ctx.fill();
        ctx.globalAlpha = 1.0;
      }
    }
  }

  function onPointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    const ne = e.nativeEvent;
    const cur = drawingRef.current;
    if (!cur || cur.pointerId !== ne.pointerId) return;
    e.preventDefault();
    if (tool === 'eraser') {
      eraseAt(ne);
      return;
    }
    const p = pointFromEvent(ne);
    cur.points.push(p);
    // Incremental render du segment courant
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext('2d');
    if (!ctx || cur.points.length < 2) return;
    const a = cur.points[cur.points.length - 2]!;
    const b = cur.points[cur.points.length - 1]!;
    drawSegment(ctx, a, b, size.w, size.h, color, width, tool);
  }

  function onPointerUp(e: React.PointerEvent<HTMLCanvasElement>) {
    const ne = e.nativeEvent;
    const cur = drawingRef.current;
    if (!cur || cur.pointerId !== ne.pointerId) return;
    e.preventDefault();
    const canvas = canvasRef.current!;
    try {
      canvas.releasePointerCapture(ne.pointerId);
    } catch {
      /* ignore */
    }
    if (tool !== 'eraser' && cur.points.length > 0) {
      onStrokeComplete({
        page_number: _pageNumber,
        stroke_data: { points: cur.points },
        color,
        width,
        tool: tool === 'highlighter' ? 'highlighter' : 'pen',
      });
    }
    drawingRef.current = null;
  }

  function eraseAt(ne: PointerEvent) {
    if (size.w === 0) return;
    const canvas = canvasRef.current!;
    const r = canvas.getBoundingClientRect();
    const x = (ne.clientX - r.left) / r.width;
    const y = (ne.clientY - r.top) / r.height;
    // Threshold = width courant + padding, en coords pct
    const threshSq = Math.pow(Math.max(width, 12) / size.w, 2);
    for (const s of strokes) {
      if (s.deleted) continue;
      const pts = s.draft.stroke_data.points;
      for (let i = 1; i < pts.length; i++) {
        const a = pts[i - 1]!;
        const b = pts[i]!;
        if (distPointSegmentSq(x, y, a[0], a[1], b[0], b[1]) <= threshSq) {
          onStrokeErase(s.id);
          break;
        }
      }
    }
  }

  // L'enabled=false rend pointer-events: none → laisse passer le scroll/zoom.
  const canDraw = enabled;
  return (
    <div
      ref={containerRef}
      className="absolute inset-0"
      style={{ pointerEvents: canDraw ? 'auto' : 'none' }}
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0"
        style={{
          touchAction: canDraw ? 'none' : 'pan-y pinch-zoom',
          cursor: canDraw ? 'crosshair' : 'default',
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      />
    </div>
  );
}

function drawStroke(ctx: CanvasRenderingContext2D, draft: StrokeDraftInput, W: number, H: number) {
  const pts = draft.stroke_data.points;
  if (pts.length === 0) return;
  if (pts.length === 1) {
    const p = pts[0]!;
    ctx.beginPath();
    ctx.fillStyle = draft.color;
    ctx.globalAlpha = draft.tool === 'highlighter' ? 0.35 : 1.0;
    ctx.arc(p[0] * W, p[1] * H, (draft.width * (p[2] || 1)) / 2, 0, 2 * Math.PI);
    ctx.fill();
    ctx.globalAlpha = 1.0;
    return;
  }
  for (let i = 1; i < pts.length; i++) {
    drawSegment(ctx, pts[i - 1]!, pts[i]!, W, H, draft.color, draft.width, draft.tool);
  }
}

function drawSegment(
  ctx: CanvasRenderingContext2D,
  a: [number, number, number, number],
  b: [number, number, number, number],
  W: number,
  H: number,
  color: string,
  width: number,
  tool: StrokeTool | 'eraser'
) {
  const ax = a[0] * W;
  const ay = a[1] * H;
  const bx = b[0] * W;
  const by = b[1] * H;
  const p = (a[2] + b[2]) / 2;
  ctx.beginPath();
  ctx.strokeStyle = color;
  ctx.lineWidth = width * (p || 1);
  ctx.globalAlpha = tool === 'highlighter' ? 0.35 : 1.0;
  ctx.moveTo(ax, ay);
  ctx.lineTo(bx, by);
  ctx.stroke();
  ctx.globalAlpha = 1.0;
}
